// opportunitySignals.js — BL-011.3 (Opportunity Intelligence Framework)
//
// Authenticated data-access layer for the Opportunity Intelligence API
// (/get-opportunity-signals). Same JWT-Bearer pattern as lib/dashboardMetrics:
// pull the live access token from the Supabase session and call the read-only
// Netlify function. Returns the shared { data, error } shape and never throws.
//
// The endpoint returns CROSS-USER, market-level DERIVED signals (Rentava-owned
// Opportunity Intelligence) — derived statistics only, k-anonymity-gated, with
// NO user identifier. It is SIGNAL data (ADR-044); the presentation layer frames
// it and carries the not-advice clarifier.

import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};

const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

/**
 * Fetch Opportunity Intelligence signals. With no params, returns the platform
 * signals for the markets the caller has engaged: { signals: [...], entitlement }.
 * With { zip } or { city, state }, returns a single market: { market, signal,
 * servable, sampleSize, entitlement }. Returns { data: payload, error }.
 */
export async function fetchOpportunitySignals(params = {}) {
  if (!supabase) return NOT_CONFIGURED;

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;

  const qs = new URLSearchParams();
  if (params.zip) qs.set('zip', params.zip);
  if (params.city) qs.set('city', params.city);
  if (params.state) qs.set('state', params.state);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  try {
    const response = await fetch(`${API_BASE_URL}/get-opportunity-signals${suffix}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}
